import { SupportV2WipeRunStatus } from "@redotech/cockroachdb-codegen/tables/support-v2-wipe-run-status";
import { SupportV2WipeStepStatus } from "@redotech/cockroachdb-codegen/tables/support-v2-wipe-step-status";
import * as z from "zod";

export const WipeRunStatus = z.enum(SupportV2WipeRunStatus);
export type WipeRunStatus = z.infer<typeof WipeRunStatus>;

export const WipeStepStatus = z.enum(SupportV2WipeStepStatus);
export type WipeStepStatus = z.infer<typeof WipeStepStatus>;

/** A run is "active" while it is still pending or in progress. */
export function isActiveWipeRunStatus(
  status: WipeRunStatus | null | undefined,
): boolean {
  return status === "pending" || status === "running";
}

/**
 * The ordered, FK-safe list of logical wipe targets. Each entry becomes one
 * durable `support_v2_wipe_step` row so the UI can poll a per-target delete
 * count, mirroring how the migrate card polls per-phase counts.
 *
 * `table` is the durable step identifier (`support_v2_wipe_step.table_name`)
 * and the actual table the delete loop targets; `label` is the human string the
 * card renders. Order matches the deletion order enforced by
 * `SupportV2TeardownService`.
 */
export const WIPE_STEPS = [
  { table: "integration_tag_mapping", label: "Integration tag mappings" },
  { table: "message_topic_link", label: "Message ↔ topic links" },
  { table: "topic_channel_thread", label: "Topic ↔ thread links" },
  { table: "channel_thread", label: "Conversation threads & messages" },
  { table: "topic", label: "Conversations & tickets (topic graph)" },
  { table: "shopper_ai_integration", label: "Integrations" },
  { table: "shopper_ai_tag", label: "Tags" },
  { table: "shopper_ai_tag_group", label: "Tag groups" },
  { table: "macro", label: "Macros" },
  { table: "user_group", label: "User groups" },
  { table: "default_rule_config", label: "Default rule config" },
  { table: "support_v2_folder", label: "Folders" },
  { table: "support_v2_view", label: "Views" },
  { table: "voice_user_availability", label: "Voice availability" },
  { table: "shopper_ai_account_setting", label: "Account settings" },
  { table: "concierge_ab_test", label: "Concierge A/B tests" },
  { table: "counter", label: "Ticket number counter" },
  { table: "support_v2_migration_run", label: "Migration history" },
] as const;

export type WipeStepTable = (typeof WIPE_STEPS)[number]["table"];

/** Human label for a wipe target; falls back to the raw table name. */
export function wipeStepLabel(table: string): string {
  return WIPE_STEPS.find((step) => step.table === table)?.label ?? table;
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { SupportV2WipeRunStatus } from "@redotech/cockroachdb-codegen/tables/support-v2-wipe-run-status";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { SupportV2WipeStepStatus } from "@redotech/cockroachdb-codegen/tables/support-v2-wipe-step-status";
